import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  ThemeProvider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GridColDef } from '@mui/x-data-grid';
 
import { theme } from '../../../theme';

import RestaurantIcon from '@mui/icons-material/Restaurant';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import KitchenIcon from '@mui/icons-material/Kitchen';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RoomIcon from '@mui/icons-material/Room';
import PersonIcon from '@mui/icons-material/Person';
import CommonDataGrid from '../../Stateless/CommonDataGrid';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { getPresignedUrlForImage } from '../../HelperFunctions/s3Service';
import { HimmaContext } from '../../../context/HimmaContext';

// Map API statuses to display labels and icon components
const statusMapping: Record<string, { label: string; Icon: React.ElementType<SvgIconProps> }> = {
  'Order Placed': { label: 'Order Placed', Icon: RestaurantIcon },
  'Delivery Partner Assigned': { label: 'Delivery Partner Assigned', Icon: CheckCircleIcon },
  'Delivery Partner Accepted': { label: 'Delivery Partner Accepted', Icon: CheckCircleIcon },
  'Merchant Accepted': { label: 'Merchant Accepted', Icon: CheckCircleIcon },
  'Merchant Rejected': { label: 'Merchant Rejected', Icon: CancelIcon },
  'Order Preparing': { label: 'Order Preparing', Icon: KitchenIcon },
  'Order Dispatched': { label: 'Order Dispatched', Icon: LocalShippingIcon },
  'Order Arrived': { label: 'Order Arrived', Icon: RoomIcon },
  'Order Delivered': { label: 'Order Delivered', Icon: PersonIcon },
  'Order Cancelled': { label: 'Order Cancelled', Icon: CancelIcon },
};


// Render an icon component with consistent props
const renderStepIcon = (Icon: React.ElementType<SvgIconProps>) => {
  const Component = Icon || CheckCircleIcon;
  return <Component fontSize="medium" color="inherit" />;
};


function renderLocalizedField(field: any) {
  if (typeof field === 'object' && field !== null) {
    return field.en || Object.values(field)[0] || '';
  }
  return field ?? '';
}

const TrackOrder: React.FC<{ trackingOrder: any }> = ({ trackingOrder }) => {
  console.log('Tracking Order:', trackingOrder);
  const customerInfo = trackingOrder.customerInfo;
  const shippingAddress = trackingOrder.shippingAddress;

  const apiClient = useContext(HimmaContext);
  const [orderDeliveryImageUrl, setOrderDeliveryImageUrl] = useState<string | null>(null);
  const [orderPickupImageUrl, setOrderPickupImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const lastFetchedOrderRef = useRef<string | number | null>(null);

  // Fetch presigned URLs for images
  useEffect(() => {
    if (!apiClient || !trackingOrder) return;

    // prevent repeated calls for the same order
    if (lastFetchedOrderRef.current === trackingOrder.orderNumber) return;
    lastFetchedOrderRef.current = trackingOrder.orderNumber;

    const fetchImageUrls = async () => {
      setImageLoading(true);
      try {
        const promises = [];

        if (trackingOrder.orderDeliveryImage) {
          promises.push(
            getPresignedUrlForImage(trackingOrder.orderDeliveryImage, apiClient)
              .then(url => setOrderDeliveryImageUrl(url))
              .catch(err => console.error('Error fetching delivery image URL:', err))
          );
        }

        if (trackingOrder.orderPickupImage) {
          promises.push(
            getPresignedUrlForImage(trackingOrder.orderPickupImage, apiClient)
              .then(url => setOrderPickupImageUrl(url))
              .catch(err => console.error('Error fetching pickup image URL:', err))
          );
        }

        await Promise.all(promises);
      } catch (error) {
        console.error('Error fetching image URLs:', error);
      } finally {
        setImageLoading(false);
      }
    };

    fetchImageUrls();
  }, [trackingOrder, apiClient]);

  // Function to get the appropriate delivery partner details
  const getDeliveryPartnerDetails = () => {
    const { orderDeliveryDetails } = trackingOrder;
    
    if (!orderDeliveryDetails) {
      return null;
    }

    const { 
      acceptedDeliveryPartners = [], 
      assignedDeliveryPartners = [], 
      deniedDeliveryPartners = [] 
    } = orderDeliveryDetails;

    // First priority: Check for accepted delivery partners
    if (acceptedDeliveryPartners && acceptedDeliveryPartners.length > 0) {
      return { partner: acceptedDeliveryPartners[0], source: 'accepted' } as const; // Return first accepted partner
    }

    // Second priority: Check for assigned delivery partners that are not denied
    if (assignedDeliveryPartners && assignedDeliveryPartners.length > 0) {
      // Filter out denied partners
      const availablePartners = assignedDeliveryPartners.filter((assignedPartner: any) => {
        return !deniedDeliveryPartners.some((deniedPartner: any) => 
          deniedPartner.deliveryPartnerId === assignedPartner.deliveryPartnerId
        );
      });

      if (availablePartners.length > 0) {
        return { partner: availablePartners[0], source: 'assigned' } as const; // Return first available assigned partner
      }
    }

    return null; // No delivery partner available
  };

  const deliveryPartnerInfo = getDeliveryPartnerDetails();

  console.log('Delivery Partner Details:', deliveryPartnerInfo);

  // Build data for partner grids
  const deliveryDetails = trackingOrder?.orderDeliveryDetails || {};
  const acceptedPartners = Array.isArray(deliveryDetails.acceptedDeliveryPartners)
    ? deliveryDetails.acceptedDeliveryPartners
    : [];
  const assignedPartners = Array.isArray(deliveryDetails.assignedDeliveryPartners)
    ? deliveryDetails.assignedDeliveryPartners
    : [];
  const deniedPartners = Array.isArray(deliveryDetails.deniedDeliveryPartners)
    ? deliveryDetails.deniedDeliveryPartners
    : [];

  const formatDateTime = (value: any) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return String(value);
    }
  };

  // Columns per section
  const basePartnerColumns: GridColDef[] = [
    { field: 'sno', headerName: 'S.No', width: 80 },
    { field: 'deliveryPartnerName', headerName: 'Name', flex: 1 },
    { field: 'deliveryPartnerPhone', headerName: 'Phone', flex: 1 },
  ];

  const acceptedColumns: GridColDef[] = [
    ...basePartnerColumns,
    { field: 'acceptedAt', headerName: 'Accepted Time', flex: 1, valueFormatter: (v) => formatDateTime(v) },
    { field: 'assignedAt', headerName: 'Assigned Time', flex: 1, valueFormatter: (v) => formatDateTime(v) },
  ];

  const assignedColumns: GridColDef[] = [
    ...basePartnerColumns,
    { field: 'assignedAt', headerName: 'Assigned Time', flex: 1, valueFormatter: (v) => formatDateTime(v) },
  ];

  const deniedColumns: GridColDef[] = [
    ...basePartnerColumns,
    { field: 'denialType', headerName: 'Denial Type', flex: 1 },
    { field: 'denialReason', headerName: 'Denial Reason', flex: 1 },
    { field: 'assignedAt', headerName: 'Assigned Time', flex: 1, valueFormatter: (v) => formatDateTime(v) },
    { field: 'deniedAt', headerName: 'Denied At', flex: 1, valueFormatter: (v) => formatDateTime(v) },
  ];

  const mapRows = (items: any[]) =>
    (items || []).map((row: any, index: number) => ({
      ...row,
      id: row.deliveryPartnerId || `${index}`,
      sno: index + 1,
    }));

  const acceptedRows = mapRows(acceptedPartners);
  const assignedRows = mapRows(assignedPartners);
  const deniedRows = mapRows(deniedPartners);

  const [showHistory, setShowHistory] = useState(false);

  // Build common tracking steps for the entire order (top summary)
  const completedStatuses = trackingOrder?.trackingHistory?.map((h: any) => h.status) || [];
  const trackingSteps = (trackingOrder?.trackingHistory || []).map((historyItem: any) => {
    const status = historyItem.status;
    const mapping = statusMapping[status as keyof typeof statusMapping] || {
      label: status,
      Icon: CheckCircleIcon,
    };
    return {
      ...mapping,
      timestamp: historyItem.timestamp,
      status: status,
    };
  });

  return (
    <ThemeProvider theme={theme}>
      <Box p={2}>
        {/* Header */}
        <Box mb={2} display="flex" justifyContent="space-between">
          <Typography variant="body2" color="textSecondary">
            <b>Order Date:</b> {new Date(trackingOrder.orderDate).toLocaleString()}
          </Typography>
          <Typography variant="body2" color="primary">
            <b>Order ID:</b> {trackingOrder.orderNumber}
          </Typography>
        </Box>

        {/* Customer Details */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Customer Details
            </Typography>
            <Typography>{renderLocalizedField(customerInfo?.name)}</Typography>
            <Typography>{renderLocalizedField(shippingAddress?.phoneNumber)}</Typography>
            <Typography>{renderLocalizedField(customerInfo?.email)}</Typography>
            <Typography>{renderLocalizedField(shippingAddress?.addressLine)}</Typography>
          </CardContent>
        </Card>

        {/* Common Order Tracking (Top Summary) */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Order Tracking
            </Typography>
            <Box mt={1} sx={{ width: '100%' }}>
              <Stepper alternativeLabel>
                {trackingSteps.map((step: any, stepIndex: number) => {
                  const isCompleted = completedStatuses.includes(step.status);
                  const isRejected = step.status.includes('Rejected') || step.status.includes('Cancelled');

                  const iconColor = isRejected
                    ? theme.palette.error.main
                    : isCompleted
                    ? theme.palette.success.main
                    : theme.palette.text.disabled;

                  return (
                    <Step key={`${step.status}-${stepIndex}`} completed={isCompleted}>
                      <StepLabel
                        StepIconComponent={() => (
                          <Box
                            color={iconColor}
                            display="flex"
                            justifyContent="center"
                          >
                            {renderStepIcon(step.Icon)}
                          </Box>
                        )}
                        sx={{
                          '& .MuiStepLabel-label': {
                            color: iconColor,
                            fontWeight: isCompleted ? 600 : 400,
                          },
                        }}
                      >
                        {step.label}
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>
          </CardContent>
        </Card>

        {/* Stores & Delivery Partner */}
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2} mb={2}>
          {/* Stores list from storePickupStatuses */}
            <Box display="grid" gap={2}>
              {(trackingOrder?.storePickupStatuses || []).map((store: any, idx: number) => (
                <Card key={`${store.storeId || idx}`} variant="outlined">
                  <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      src={
                        Array.isArray(trackingOrder.orderItems?.[0]?.imageUrl)
                          ? trackingOrder.orderItems[0].imageUrl[0]
                          : trackingOrder.orderItems?.[0]?.imageUrl ||
                            'https://img.freepik.com/free-vector/restaurant-interior_23-2147513538.jpg'
                      }
                      alt={renderLocalizedField(store.storeName) || 'Store'}
                      sx={{ width: 100, height: 100, mr: 2 }}
                      variant="square"
                    />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {renderLocalizedField(store.storeName) || 'Store Name'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {renderLocalizedField(store.storeAddress) || 'Address not provided'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {renderLocalizedField(store.storeType) || ''}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
 

          {/* Delivery partner card */}
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Delivery Partner Details
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => setShowHistory(true)} 
                    sx={{ textTransform: 'none', marginLeft:'20px' }}
                  >
                    History
                  </Button>
                </Box>
                {deliveryPartnerInfo ? (
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {deliveryPartnerInfo.partner.deliveryPartnerName}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: deliveryPartnerInfo.source === 'accepted' ? theme.palette.success.main : theme.palette.info.main }}>
                      {deliveryPartnerInfo.source === 'accepted' ? 'Accepted' : 'Assigned'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Phone: {deliveryPartnerInfo.partner.deliveryPartnerPhone}
                    </Typography>
                    {deliveryPartnerInfo.partner.driverAcceptedTime && (
                      <Typography variant="body2" color="textSecondary">
                        Accepted: {new Date(deliveryPartnerInfo.partner.driverAcceptedTime).toLocaleString()}
                      </Typography>
                    )}
                    {deliveryPartnerInfo.partner.estimatedDeliveryTime && (
                      <Typography variant="body2" color="textSecondary">
                        Estimated Delivery: {new Date(deliveryPartnerInfo.partner.estimatedDeliveryTime).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography color="textSecondary">Not Yet Assigned</Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>


        <Dialog open={showHistory} onClose={() => setShowHistory(false)} fullWidth maxWidth="lg">
          <DialogTitle sx={{ pr: 5, position: 'relative' }}>
            Delivery Partner History
            <IconButton
              aria-label="close"
              onClick={() => setShowHistory(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (themeArg) => themeArg.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box display="grid" gap={2}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Accepted Delivery Partners
                  </Typography>
                  <CommonDataGrid
                    rows={acceptedRows}
                    columns={acceptedColumns}
                    pageSize={5}
                    onPageSizeChange={() => {}}
                    checkboxSelection={false}
                    loading={false}
                    page={0}
                    rowCount={acceptedRows.length}
                    components={{}}
                  />
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Assigned Delivery Partners
                  </Typography>
                  <CommonDataGrid
                    rows={assignedRows}
                    columns={assignedColumns}
                    pageSize={5}
                    onPageSizeChange={() => {}}
                    checkboxSelection={false}
                    loading={false}
                    page={0}
                    rowCount={assignedRows.length}
                    components={{}}
                  />
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Denied Delivery Partners
                  </Typography>
                  <CommonDataGrid
                    rows={deniedRows}
                    columns={deniedColumns}
                    pageSize={5}
                    onPageSizeChange={() => {}}
                    checkboxSelection={false}
                    loading={false}
                    page={0}
                    rowCount={deniedRows.length}
                    components={{}}
                  />
                </CardContent>
              </Card>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Items list (no per-item tracking) */}
        {trackingOrder.orderItems?.map((item: any, index: number) => {
          return (
            <Card variant="outlined" key={index} sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar
                    src={
                      Array.isArray(item?.imageUrl)
                        ? item.imageUrl[0]
                        : item.imageUrl
                    }
                    alt={renderLocalizedField(item?.itemName)}
                    sx={{ width: 60, height: 60 }}
                    variant="square"
                  />
                  <Typography fontWeight={600}>
                    {renderLocalizedField(item?.itemName)}
                  </Typography>
                  <Typography fontWeight={500}>
                    Item Price : ₹ {item?.originalPrice?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
                {/* Choices (from API: orderItems[].choices) shown above special instructions */}
                {Array.isArray(item?.choices) && item.choices.length > 0 && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                    {'+ '}
                    {item.choices
                      .map(
                        (c: any) =>
                          `${renderLocalizedField(c?.choiceName)} (₹ ${Number(c?.choicePrice ?? 0).toFixed(2)})`
                      )
                      .join(', ')}
                  </Typography>
                )}
                {/* Special Instructions */}
                {item?.specialInstructions && item?.specialInstructions?.trim() !== 'N/A' && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                    <b>special Instructions:</b> {item?.specialInstructions}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
        
        {/* Order Images */}
        {(orderDeliveryImageUrl || orderPickupImageUrl) && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Order Images
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                {orderPickupImageUrl && (
                  <Box>
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      Pickup Image
                    </Typography>
                    <Avatar
                      src={orderPickupImageUrl}
                      alt="Order Pickup"
                      sx={{ width: 120, height: 120, borderRadius: 1 }}
                      variant="square"
                    />
                  </Box>
                )}
                {orderDeliveryImageUrl && (
                  <Box>
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      Delivery Image
                    </Typography>
                    <Avatar
                      src={orderDeliveryImageUrl}
                      alt="Order Delivery"
                      sx={{ width: 120, height: 120, borderRadius: 1 }}
                      variant="square"
                    />
                  </Box>
                )}
              </Box>
              {imageLoading && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Loading images...
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default TrackOrder;
